import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'VisionSet - Build Computer Vision Datasets in Minutes | Fast Dataset Creation Tool',
    description: 'Stop wasting hours hunting images. VisionSet turns natural-language descriptions into clean, exportable computer vision datasets fast. Swipe to curate, refine results, download ZIP. 500 free credits, no card required.',
    keywords: 'computer vision datasets, dataset creation tool, image dataset builder, CV training data, machine learning datasets',
    canonical: '/',
    ogTitle: 'VisionSet - Build Computer Vision Datasets in Minutes',
    ogDescription: 'Stop wasting hours hunting images. VisionSet turns natural-language descriptions into clean, exportable computer vision datasets fast.',
    ogUrl: '/',
    twitterTitle: 'VisionSet - Build Computer Vision Datasets in Minutes',
    twitterDescription: 'Stop wasting hours hunting images. VisionSet turns natural-language descriptions into clean, exportable computer vision datasets fast.',
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "VisionSet",
        "url": siteUrl,
        "applicationCategory": "DeveloperApplication",
        "description": "Build computer vision datasets in minutes. Stop wasting hours hunting images, removing duplicates, and organizing folders. VisionSet turns a natural-language description into a clean, exportable dataset—fast.",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "500 credits free, no card required"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "ratingCount": "127"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Can I export and use these images commercially?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You are responsible for how you use exported images. VisionSet helps you discover and curate images quickly."
            }
          },
          {
            "@type": "Question",
            "name": "Do I get charged for searches?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. Credits only apply to images delivered into your swipe pool."
            }
          },
          {
            "@type": "Question",
            "name": "Do you download images immediately?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. Images are collected as candidates and only downloaded at export."
            }
          },
          {
            "@type": "Question",
            "name": "What if I'm not getting good results?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Use 'Get more like these' to refine results based on what you keep."
            }
          },
          {
            "@type": "Question",
            "name": "Do I need to code?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No."
            }
          }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "VisionSet - Build Computer Vision Datasets in Minutes",
        "url": siteUrl,
        "description": "Stop wasting hours hunting images. VisionSet turns natural-language descriptions into clean, exportable computer vision datasets fast.",
        "publisher": {
          "@type": "Organization",
          "name": "Ausonio Algorithmics LLC",
          "url": "https://www.ausonioalg.com/"
        }
      }
    ]
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/4126e18b1195bdf88076d12182861244/e0a391f1061e2e4527f1f332a6fc5bcf.png" alt="VisionScout" className="h-10" />
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">How it works</a>
            <a href="#pricing" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Pricing</a>
            <a href="#faq" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">FAQ</a>
            <a href="https://vision-scout.ausonioalg.com/blog/" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowra
            p cursor-pointer">Blog</a>
            <a href="https://vision-scout.replit.app/" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowra
            p cursor-pointer">Login</a>
          </nav>

          <div className="flex items-center gap-3">
            <div className="flex flex-col items-end">
              <a href="https://vision-scout.replit.app/" className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-6 py-2.5 rounded-lg font-medium text-sm hover:from-teal-700 hover:to-emerald-700 transition-all hover:shadow-lg whitespace-nowrap cursor-pointer">
                Start free
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-teal-50/30 to-emerald-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h1 className="text-6xl font-bold text-slate-900 leading-tight mb-6">
                Build computer vision datasets in minutes.
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                Stop wasting hours hunting images, removing duplicates, and organizing folders.
                VisionScout turns a natural-language description into a clean, exportable dataset—fast.
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8">
                <div className="flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium border border-teal-200">
                  <i className="ri-check-line"></i>
                  Swipe to curate (keep / reject in seconds)
                </div>
                <div className="flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium border border-teal-200">
                  <i className="ri-check-line"></i>
                  Get more like these (results improve as you go)
                </div>
                <div className="flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium border border-teal-200">
                  <i className="ri-check-line"></i>
                  Download a ZIP (ready for training, instantly)
                </div>
              </div>

              <div className="flex items-center gap-4">
                <a href="https://vision-scout.replit.app/" className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-teal-700 hover:to-emerald-700 transition-all hover:shadow-xl whitespace-nowrap cursor-pointer">
                  Start free (500 credits)
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 transform hover:scale-105 transition-transform duration-300 border border-slate-200">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=A%20golden%20retriever%20dog%20sitting%20next%20to%20a%20red%20tractor%20in%20a%20farm%20field%2C%20bright%20daylight%2C%20clean%20simple%20background%2C%20professional%20photography%20style%20for%20computer%20vision%20training%20dataset&width=600&height=400&seq=hero-mockup-001&orientation=landscape" 
                    alt="Dataset example" 
                    className="w-full h-80 object-cover rounded-lg"
                  />
                  <div className="absolute top-4 right-4 bg-slate-900 text-white px-3 py-1 rounded-full text-sm font-medium">
                    42 / 200
                  </div>
                </div>
                
                <div className="flex gap-3 mt-4">
                  <button className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-md">
                    <i className="ri-arrow-left-line"></i> Reject
                  </button>
                  <button className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-md">
                    Keep <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
                
                <button className="w-full mt-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-colors whitespace-nowrap cursor-pointer shadow-md">
                  Get more like these
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-6xl font-bold text-slate-900 text-center mb-16">
            Dataset creation is the hidden tax on every CV project.
          </h2>
          
          <div className="space-y-10 mb-16">
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-900 leading-relaxed mb-3">
                You don't lose time training models.
              </p>
              <p className="text-3xl font-bold text-red-600 leading-relaxed">
                You lose time finding data.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 via-orange-50 to-red-50 rounded-2xl p-14 shadow-lg border border-red-100">
              <div className="grid md:grid-cols-2 gap-10">
                <div className="flex items-start gap-6 bg-white rounded-xl p-10 shadow-sm hover:shadow-md transition-shadow border border-slate-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-5xl text-red-600"></i>
                  </div>
                  <div>
                    <p className="text-3xl font-semibold text-slate-800">Searching the web for hours</p>
                    <p className="text-xl text-slate-500 mt-3">Endless scrolling, tab overload</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white rounded-xl p-10 shadow-sm hover:shadow-md transition-shadow border border-slate-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-copy-line text-5xl text-orange-600"></i>
                  </div>
                  <div>
                    <p className="text-3xl font-semibold text-slate-800">Copy-pasting images one by one</p>
                    <p className="text-xl text-slate-500 mt-3">Manual, tedious, soul-crushing</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white rounded-xl p-10 shadow-sm hover:shadow-md transition-shadow border border-slate-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-spam-line text-5xl text-red-600"></i>
                  </div>
                  <div>
                    <p className="text-3xl font-semibold text-slate-800">Getting duplicates and junk</p>
                    <p className="text-xl text-slate-500 mt-3">Low quality, irrelevant results</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white rounded-xl p-10 shadow-sm hover:shadow-md transition-shadow border border-slate-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-5xl text-orange-600"></i>
                  </div>
                  <div>
                    <p className="text-3xl font-semibold text-slate-800">Realizing the dataset is missing edge cases</p>
                    <p className="text-xl text-slate-500 mt-3">Back to square one</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white rounded-xl p-10 shadow-sm hover:shadow-md transition-shadow md:col-span-2 border border-slate-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-restart-line text-5xl text-red-600"></i>
                  </div>
                  <div>
                    <p className="text-3xl font-semibold text-slate-800">Starting over</p>
                    <p className="text-xl text-slate-500 mt-3">The cycle repeats</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-50 rounded-2xl p-10 shadow-lg border-2 border-emerald-200">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-md">
                  <i className="ri-check-double-line text-3xl text-white"></i>
                </div>
                <p className="text-4xl font-bold text-slate-900">
                  VisionScout removes the grind.
                </p>
              </div>
              <p className="text-xl text-slate-600">Get back to building models, not hunting pixels.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Outcome Section */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-slate-50 via-teal-50/30 to-emerald-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-slate-900 text-center mb-16">
            From idea → dataset, in one workflow.
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-200">
              <div className="relative w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center mb-6 shadow-md">
                <span className="absolute -top-2 -left-2 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">1</span>
                <i className="ri-edit-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Describe</h3>
              <p className="text-slate-600 mb-4">Type what you need. Example: "Dogs next to tractors."</p>
              <p className="text-sm italic text-slate-400">Natural language. No syntax.</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-200">
              <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mb-6 shadow-md">
                <span className="absolute -top-2 -left-2 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">2</span>
                <i className="ri-arrow-left-right-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Swipe</h3>
              <p className="text-slate-600 mb-4">Keep what works. Reject what doesn't. Keyboard shortcuts included.</p>
              <p className="text-sm italic text-slate-400">Fast curation. No clicking.</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-200">
              <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-6 shadow-md">
                <span className="absolute -top-2 -left-2 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">3</span>
                <i className="ri-download-cloud-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Export</h3>
              <p className="text-slate-600 mb-4">Download a clean ZIP. No scripts. No mess.</p>
              <p className="text-sm italic text-slate-400">Training-ready. Instantly.</p>
            </div>
          </div>

          <div className="text-center">
            <a href="https://vision-scout.replit.app/" className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-teal-700 hover:to-emerald-700 transition-all hover:shadow-xl whitespace-nowrap cursor-pointer inline-block">
              Start free
            </a>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-slate-900 text-center mb-16">
            Better than manual search. Faster than writing scrapers.
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 border-2 border-red-200 shadow-lg">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-search-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-red-700">Manual image search</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">Slow and tedious</span>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">Endlessly repetitive</span>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">No organization</span>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">Duplicates everywhere</span>
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t-2 border-red-200">
                  <p className="text-lg text-red-800 font-semibold italic">Hours wasted. Every. Single. Time.</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 border-2 border-red-200 shadow-lg">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-code-s-slash-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-red-700">DIY scripts / scrapers</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">Hours of setup time</span>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">Break constantly</span>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">Ongoing maintenance</span>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-close-circle-fill text-red-500 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <span className="text-xl font-medium">Not reusable for new datasets</span>
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t-2 border-red-200">
                  <p className="text-lg text-red-800 font-semibold italic">More coding than training.</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-50 rounded-2xl p-10 border-4 border-emerald-400 shadow-2xl flex flex-col justify-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-400 rounded-full -mr-16 -mt-16 opacity-20"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-teal-400 rounded-full -ml-20 -mb-20 opacity-20"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <i className="ri-flashlight-fill text-4xl text-white"></i>
                  </div>
                  <h3 className="text-4xl font-bold text-emerald-800">VisionScout</h3>
                </div>
                
                <p className="text-2xl font-bold text-slate-900 mb-6 leading-tight">
                  The only tool built specifically for CV dataset creation.
                </p>
                
                <ul className="space-y-5 mb-8">
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-check-double-fill text-emerald-600 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <div>
                      <span className="text-xl font-semibold block">One unified interface</span>
                      <span className="text-base text-slate-600">No switching between tools</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-check-double-fill text-emerald-600 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <div>
                      <span className="text-xl font-semibold block">Lightning-fast curation</span>
                      <span className="text-base text-slate-600">Swipe through 50 images in under a minute</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-check-double-fill text-emerald-600 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <div>
                      <span className="text-xl font-semibold block">Iterative refinement</span>
                      <span className="text-base text-slate-600">Results improve as you curate</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-check-double-fill text-emerald-600 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <div>
                      <span className="text-xl font-semibold block">Export-ready output</span>
                      <span className="text-base text-slate-600">Clean ZIP, organized folders, zero cleanup</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4 text-slate-800">
                    <i className="ri-check-double-fill text-emerald-600 text-3xl flex-shrink-0 w-8 h-8 flex items-center justify-center"></i>
                    <div>
                      <span className="text-xl font-semibold block">Multi-source retrieval</span>
                      <span className="text-base text-slate-600">Better coverage, fewer gaps</span>
                    </div>
                  </li>
                </ul>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-emerald-300">
                  <p className="text-xl font-bold text-emerald-800 mb-3">
                    <i className="ri-time-line mr-2"></i>
                    From zero to training-ready dataset in under 30 minutes.
                  </p>
                  <p className="text-lg text-slate-700">
                    Stop fighting tools. Start building models.
                  </p>
                </div>
                
                <a href="https://vision-scout.replit.app/" className="w-full mt-8 bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-5 rounded-xl font-bold text-xl hover:from-emerald-700 hover:to-teal-700 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer inline-block text-center">
                  Start free (500 credits) →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Use Cases Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-teal-50/30 to-emerald-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-slate-900 text-center mb-6">
            Built for CV builders who need data now.
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-teal-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className="ri-robot-line text-5xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Robotics object datasets</h3>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-orange-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className="ri-tools-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Industrial equipment datasets</h3>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-emerald-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-5xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Safety / compliance edge cases</h3>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-emerald-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className="ri-plant-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Agriculture / wildlife datasets</h3>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-teal-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-5xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Classroom projects &amp; research</h3>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-red-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className="ri-rocket-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900">MVP model validation</h3>
            </div>
          </div>

          <p className="text-center text-lg italic text-slate-600">
            If you can describe it, you can build a dataset for it.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-slate-900 text-center mb-4">
            Simple pricing. Pay for images, not complexity.
          </h2>
          <p className="text-xl text-slate-600 text-center mb-16">
            1 credit = 1 image delivered to your swipe pool.<br />
            (Kept or rejected—your choice.)
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Free Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Free</h3>
              <div className="mb-6">
                <p className="text-4xl font-bold text-slate-900">$0</p>
                <p className="text-teal-600 font-semibold mt-2">500 credits lifetime</p>
              </div>
              
              <hr className="my-6 border-slate-200" />
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>1 project</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>Images expire in 7 days</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>ZIP export</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>No credit card required</span>
                </li>
              </ul>
              
              <button className="w-full border-2 border-teal-600 text-teal-600 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer">
                Start free
              </button>
            </div>

            {/* Starter Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-teal-500 transform md:scale-105 hover:shadow-2xl transition-all">
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-4">
                POPULAR
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Starter</h3>
              <div className="mb-6">
                <p className="text-4xl font-bold text-slate-900">$19<span className="text-lg text-slate-600">/mo</span></p>
                <p className="text-teal-600 font-semibold mt-2">3,000 credits/month</p>
              </div>
              
              <hr className="my-6 border-slate-200" />
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>3 projects</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>Images expire in 1 month</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>ZIP export</span>
                </li>
              </ul>
              
              <button className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-teal-700 hover:to-emerald-700 transition-colors whitespace-nowrap cursor-pointer shadow-md">
                Upgrade
              </button>
            </div>

            {/* Pro Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Pro</h3>
              <div className="mb-6">
                <p className="text-4xl font-bold text-slate-900">$39<span className="text-lg text-slate-600">/mo</span></p>
                <p className="text-teal-600 font-semibold mt-2">7,000 credits/month</p>
              </div>
              
              <hr className="my-6 border-slate-200" />
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>Unlimited projects</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>Images never expire</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>ZIP export</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>Buy additional credits</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-check-line text-emerald-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <span>Priority support</span>
                </li>
              </ul>
              
              <button className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-teal-700 hover:to-emerald-700 transition-all whitespace-nowrap cursor-pointer shadow-md">
                Go Pro
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-br from-slate-50 via-teal-50/30 to-emerald-50/30">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-slate-900 text-center mb-16">
            FAQ
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Can I export and use these images commercially?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                You are responsible for how you use exported images. VisionScout helps you discover and curate images quickly.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Do I get charged for searches?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                No. Credits only apply to images delivered into your swipe pool.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Do you download images immediately?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                No. Images are collected as candidates and only downloaded at export.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                What if I'm not getting good results?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Use "Get more like these" to refine results based on what you keep.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Do I need to code?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                No.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Stop paying the dataset tax.
          </h2>
          <p className="text-xl text-teal-100 mb-10">
            Build a dataset today in less time than it takes to write a scraping script.
          </p>
          
          <a href="https://vision-scout.replit.app/" className="bg-white text-teal-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-teal-50 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer inline-block">
            Start free (500 credits)
          </a>
          <p className="text-sm text-teal-100 mt-4">No credit card. Takes 60 seconds.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/4126e18b1195bdf88076d12182861244/e0a391f1061e2e4527f1f332a6fc5bcf.png" alt="VisionScout" className="h-8 mb-4 brightness-0 invert" />
              <p className="text-slate-400 text-sm">Build computer vision datasets in minutes.</p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#how-it-works" className="hover:text-teal-400 transition-colors cursor-pointer">How it works</a></li>
                <li><a href="#pricing" className="hover:text-teal-400 transition-colors cursor-pointer">Pricing</a></li>
                <li><a href="#faq" className="hover:text-teal-400 transition-colors cursor-pointer">FAQ</a></li>
                <li><a href="https://vision-scout.ausonioalg.com/blog/" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Blog</a></li>

              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/terms" className="hover:text-teal-400 transition-colors cursor-pointer">Terms</Link></li>
                <li><Link to="/privacy" className="hover:text-teal-400 transition-colors cursor-pointer">Privacy</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Contact</a></li>
                <li><a href="https://vision-scout.replit.app/" className="hover:text-teal-400 transition-colors cursor-pointer">Login</a></li>
              </ul>
            </div>
          </div>
          
          <hr className="border-slate-700 mb-6" />
          
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-400">
            <p>&copy; 2025 VisionScout. All rights reserved.</p>
            <p>Built by <a href="https://www.ausonioalg.com/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors cursor-pointer">Ausonio Algorithmics</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
